/**
 * Lazy Explore Cards section for marketing landings (PBI #1144).
 *
 * Keeps the heavy carousel / preview-modal graph out of the eager landing
 * chunk. The fallback reserves approximate chrome height to limit CLS while
 * the async chunk loads. Import this module (not ExploreCardsSection) from
 * LandingPage / IntroOfferPage.
 */

import { Suspense, lazy } from 'react';
import { Group, Skeleton, Stack } from '@mantine/core';

const ExploreCardsSection = lazy(() =>
  import('./ExploreCardsSection').then((m) => ({
    default: m.ExploreCardsSection,
  })),
);

/**
 * Height-reserved placeholder shown while the explore chunk downloads.
 */
function ExploreCardsSectionFallback() {
  return (
    <Stack gap="md" data-testid="explore-cards-fallback">
      <Skeleton height={36} width="40%" mx="auto" radius="md" />
      <Group gap="sm" justify="center" wrap="wrap">
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} height={32} width={88} radius="xl" />
        ))}
      </Group>
      <Group gap="md" wrap="nowrap" style={{ overflow: 'hidden' }}>
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} height={280} w={220} radius="md" style={{ flexShrink: 0 }} />
        ))}
      </Group>
    </Stack>
  );
}

/**
 * Suspense boundary around the lazy ExploreCardsSection.
 */
export function LazyExploreCardsSection() {
  return (
    <Suspense fallback={<ExploreCardsSectionFallback />}>
      <ExploreCardsSection />
    </Suspense>
  );
}

export default LazyExploreCardsSection;
